import { LineItemProps, ServiceOffering } from '@jbhi-fi/apis/src/services/types';
import { JbCart, JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import { JbCartItemTransformer, getOfferingForCartItem } from './transformers/JbCartItemTransformer';
import { getServiceOfferings } from '../../../utils/parseServiceOfferings';
import { ServiceIds } from '../../../utils/constants/services';
import { IsAttachedService } from '../pdp/utils/product-service';
import { AttachedServiceProps } from '@jbhi-fi/components/src/Cart/AttachedService/AttachedService';
import { BundleOfferingProps } from '@jbhi-fi/components/src/Cart/BundleOffering/BundleOffering';
import { Facets } from '../pdp/installProductService/types';

export type AttachedServiceEntry = {
  cartItem: JbCartItem;
  serviceData: ServiceOffering;
};

export type CartLineServicesParams = {
  cart?: JbCart | null;
  item: JbCartItem;
};

export type CartLineServicesResult = {
  offering?: ReturnType<typeof getOfferingForCartItem>;
  attachedService?: AttachedServiceProps | undefined;
  bundleSuggestion?: BundleOfferingProps | undefined;
  installSuggestions?: Array<LineItemProps>;
  nonInstallSuggestions?: Array<LineItemProps>;
  attachedServiceItemsForLine: Array<AttachedServiceEntry>;
  attachedInstallServices: Array<AttachedServiceEntry>;
  nonInstallServiceData?: ServiceOffering;
  shouldRenderDefaultAttachedService: boolean;
  hasInstallModule: boolean;
  hasServicesModule: boolean;
  hasMultipleServiceModules: boolean;
  shouldRenderAttachedServicesBlock: boolean;
  productFacets?: Facets;
};

export const getCartLineServices = ({ cart, item }: CartLineServicesParams): CartLineServicesResult => {
  const cartServiceOfferings = getServiceOfferings();
  const offering = getOfferingForCartItem(item, cartServiceOfferings);
  const attachedService = JbCartItemTransformer.toAttachedServiceProps(item);
  const bundleSuggestion = JbCartItemTransformer.toBundleOfferingProps(item);

  const fullOffering = offering?.fullOffering;
  const services = fullOffering?.services ?? [];

  const installServices = services.filter(service => service.id === ServiceIds.Install);
  const nonInstallServices = services.filter(service => service.id !== ServiceIds.Install && service.shouldRender);

  const installSuggestions =
    installServices.length && fullOffering
      ? ([{ ...fullOffering, services: installServices }] as Array<LineItemProps>)
      : undefined;
  const nonInstallSuggestions =
    nonInstallServices.length && fullOffering
      ? ([{ ...fullOffering, services: nonInstallServices }] as Array<LineItemProps>)
      : undefined;

  const attachedServiceItemsForLine =
    cart?.items
      ?.filter(
        cartItem => IsAttachedService(cartItem) && cartItem.properties?._serviceLink === item.properties?._serviceLink
      )
      .map(cartItem => ({
        cartItem,
        serviceData: tryParseJson<ServiceOffering>(cartItem.properties?._serviceData),
      }))
      .filter((entry): entry is AttachedServiceEntry => !!entry.serviceData) ?? [];

  const attachedInstallServices = attachedServiceItemsForLine.filter(
    entry => entry.serviceData.typeId === ServiceIds.Install
  );

  const itemServiceOfferings = (cartServiceOfferings?.[item.line_id] as Array<ServiceOffering>) ?? [];
  const extras = nonInstallServices.find(service => service.id === ServiceIds.MobileExtras);
  const nonInstallServiceId = extras?.id || nonInstallServices[0]?.id;

  const nonInstallServiceData = nonInstallServiceId
    ? itemServiceOfferings.find(serviceOffering => serviceOffering.typeId === nonInstallServiceId)
    : undefined;

  const isAttachedServiceIsInstall =
    tryParseJson<ServiceOffering>(item?.attachedService?.properties?._serviceData)?.typeId === ServiceIds.Install;

  const shouldRenderDefaultAttachedService =
    !!attachedService && (!attachedInstallServices.length || !isAttachedServiceIsInstall);

  const hasInstallModule = !!installSuggestions?.length;
  const hasServicesModule = !!(nonInstallSuggestions && nonInstallServiceData);
  const hasMultipleServiceModules = hasInstallModule && hasServicesModule;
  const shouldRenderAttachedServicesBlock =
    attachedInstallServices.length > 0 || (shouldRenderDefaultAttachedService && !!attachedService);

  const productFacets = item.onlineProduct?.Display?.SpecificationDetails?.reduce<Facets | undefined>((acc, curr) => {
    const next = acc ?? {};
    next[curr.Name] = curr?.Values;
    return next;
  }, undefined);

  return {
    offering,
    attachedService,
    bundleSuggestion,
    installSuggestions,
    nonInstallSuggestions,
    attachedServiceItemsForLine,
    attachedInstallServices,
    nonInstallServiceData,
    shouldRenderDefaultAttachedService,
    hasInstallModule,
    hasServicesModule,
    hasMultipleServiceModules,
    shouldRenderAttachedServicesBlock,
    productFacets,
  };
};
